import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getRevisionHistory } from "../../../services/api";
import { showSnackBar } from "../../../utils/commonServices";

/**
 * Loads `GET /api/v2/revisions/history` for one project.
 *
 * The history is kept in local state rather than redux: it is a read-only view
 * owned by the history popup, and `video_url` / `download_url` are presigned
 * links that expire, so they must never be cached across mounts - every open
 * refetches.
 *
 * Permission handling is deliberately response-driven. The endpoint answers 403
 * without "View Revision Page" and returns `notes_visible: false` (with no
 * `notes` key) without Notes -> View, so the UI reads the response instead of
 * re-deriving permissions locally.
 *
 * @param {number} eventId project id
 * @param {boolean} enabled fetch on mount / when it flips true
 */
export default (eventId, enabled = false) => {
  const dispatch = useDispatch();
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(!!enabled);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(
    (finalVideoId) => {
      if (!eventId) {
        // No project selected - the endpoint would answer 404 for a missing event_id.
        setHistory(null);
        setError({ http_code: 404, message: 'Event not found!!' });
        setIsLoading(false);
        return Promise.resolve();
      }

      setIsLoading(true);
      setError(null);

      const params = { event_id: eventId };
      if (finalVideoId) params.final_video_id = finalVideoId;

      return getRevisionHistory(params)
        .then((response) => {
          const responseObject = response?.data;
          if (responseObject?.http_code === 200) {
            setHistory(responseObject?.data || null);
          } else {
            setHistory(null);
            setError({ http_code: responseObject?.http_code, message: responseObject?.message });
          }
          setIsLoading(false);
        })
        .catch((error) => {
          const errorObject = error?.response?.data;
          const httpCode = errorObject?.http_code || error?.response?.status;
          setHistory(null);
          setError({ http_code: httpCode, message: errorObject?.message });
          // 401 is the only status that needs the shared handler - it logs the user out.
          // 403 / 404 / 422 are rendered inside the panel instead of as a snackbar.
          if (httpCode === 401) {
            showSnackBar(dispatch, errorObject || { http_code: 401 });
          }
          setIsLoading(false);
        });
    },
    [eventId, dispatch]
  );

  useEffect(() => {
    if (enabled) fetchHistory();
  }, [enabled, fetchHistory]);

  return {
    history,
    versions: Array.isArray(history?.versions) ? history.versions : [],
    // The key is absent (not false) only on a malformed response; treat anything
    // but an explicit false as visible and let the `notes` key decide.
    notesVisible: history?.notes_visible !== false,
    isLoading,
    error,
    fetchHistory,
  };
};
